// src/components/serverSetup/ChannelConfiguration.js

import React from 'react';
import axios from 'axios';
import PropTypes from 'prop-types'; // Import PropTypes
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Tabs,
  Tab,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Snackbar, // Import Snackbar
  Alert,     // Import Alert
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

const ChannelConfiguration = ({
  channels,
  categories,
  selectedGuild,
  accessToken,
  channel,
  setChannel,
  newChannelName,
  setNewChannelName,
  selectedCategoryId,
  setSelectedCategoryId,
  newCategoryName,
  setNewCategoryName,
  refreshChannelsAndCategories, // Receive the refresh function
}) => {
  const [tabValue, setTabValue] = React.useState(0);
  const [isCreating, setIsCreating] = React.useState(false); // State for loading indicator

  // Snackbar state specific to this component
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: '',
    severity: 'success', // 'success', 'error', 'warning', 'info'
  });

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  /**
   * Handle Tab Change between "Use Existing Channel" and "Create New Channel"
   */
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  /**
   * Handle Creation of Channel and/or Category
   */
  const handleCreateChannel = async () => {
    if (!newChannelName) {
      setSnackbar({
        open: true,
        message: 'Please enter a channel name.',
        severity: 'warning',
      });
      return;
    }

    // Updated Regex with Hyphen at the End to Prevent Range Misinterpretation
    const channelNameRegex = /^[a-z0-9_-]{2,100}$/;
    if (!channelNameRegex.test(newChannelName)) {
      setSnackbar({
        open: true,
        message: 'Channel name must be 2-100 characters long and can only contain lowercase letters, numbers, hyphens, and underscores.',
        severity: 'error',
      });
      return;
    }

    if (selectedCategoryId === 'create_new_category') {
      if (!newCategoryName) {
        setSnackbar({
          open: true,
          message: 'Please enter a new category name.',
          severity: 'warning',
        });
        return;
      }

      setIsCreating(true); // Start loading

      try {
        // Create the new category first
        const createCategoryResponse = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/discord/guilds/${selectedGuild}/channels`,
          {
            name: newCategoryName,
            type: 4, // Type 4 is category
          },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
          }
        );

        const newCategory = createCategoryResponse.data;
        const newParentId = newCategory.id;

        // Now, create the channel with the new parent_id
        const createChannelResponse = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/discord/guilds/${selectedGuild}/channels`,
          {
            name: newChannelName,
            type: 0, // Type 0 is text channel
            parent_id: newParentId,
          },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (createChannelResponse.data) {
          setSnackbar({
            open: true,
            message: 'Category and Channel created successfully!',
            severity: 'success',
          });
          // Refresh channels and categories
          refreshChannelsAndCategories();
          // Reset input fields
          setNewChannelName('');
          setNewCategoryName('');
          setSelectedCategoryId('');
        }
      } catch (error) {
        console.error('Error creating channel or category:', error);
        const errorMsg = error.response?.data?.error || error.message;
        setSnackbar({
          open: true,
          message: `Failed to create channel or category: ${errorMsg}`,
          severity: 'error',
        });
      } finally {
        setIsCreating(false); // End loading
      }
    } else {
      if (!selectedCategoryId) {
        setSnackbar({
          open: true,
          message: 'Please select a category.',
          severity: 'warning',
        });
        return;
      }

      // Check for duplicate channel names
      const duplicate = channels.some(ch => ch.name === newChannelName);
      if (duplicate) {
        setSnackbar({
          open: true,
          message: 'A channel with this name already exists. Please choose a different name.',
          severity: 'warning',
        });
        return;
      }

      setIsCreating(true); // Start loading

      try {
        const payload = {
          name: newChannelName,
          type: 0, // Type 0 is text channel
          parent_id: selectedCategoryId,
        };

        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/discord/guilds/${selectedGuild}/channels`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.data) {
          setSnackbar({
            open: true,
            message: 'Channel created successfully!',
            severity: 'success',
          });
          // Refresh channels and categories
          refreshChannelsAndCategories();
          // Reset input field
          setNewChannelName('');
        }
      } catch (error) {
        console.error('Error creating channel:', error);
        const errorMsg = error.response?.data?.error || error.message;
        setSnackbar({
          open: true,
          message: `Failed to create channel: ${errorMsg}`,
          severity: 'error',
        });
      } finally {
        setIsCreating(false); // End loading
      }
    }
  };

  return (
    <>
      <Accordion
        defaultExpanded
        sx={{
          mt: 2,
          backgroundColor: '#081529',
          backgroundImage: 'linear-gradient(145deg, #081529, #12203d)',
          '&.Mui-expanded': {
            backgroundColor: '#081529',
            backgroundImage: 'linear-gradient(145deg, #081529, #12203d)',
          },
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.5)',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: '#A1D1F0' }} />}
          sx={{
            backgroundColor: '#081529',
            color: '#E0E4ED',
            fontFamily: 'Poppins, sans-serif',
          }}
        >
          <Typography sx={{ color: '#A1D1F0', fontFamily: 'Poppins, sans-serif' }}>
            Channel Configuration
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            centered
            sx={{
              mt: 2,
              backgroundColor: '#0A1B34',
              borderRadius: '8px',
              padding: '4px',
              '& .MuiTabs-flexContainer': {
                width: '100%',
              },
              '& .MuiTab-root': {
                flex: 1,
                textTransform: 'none',
                fontSize: '1rem',
                color: '#E0E4ED',
                fontFamily: 'Poppins, sans-serif',
                '&.Mui-selected': {
                  color: '#A1D1F0',
                  borderBottom: '2px solid #A1D1F0',
                },
                '&:hover': {
                  backgroundColor: '#12203D',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#A1D1F0',
              },
            }}
          >
            <Tab label="Use Existing Channel" />
            <Tab label="Create New Channel" />
          </Tabs>
          <Box
            sx={{
              mt: 2,
              padding: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            {tabValue === 0 && (
              <FormControl fullWidth>
                <InputLabel
                  id="channel-select-label"
                  sx={{ color: '#A1D1F0', fontFamily: 'Poppins, sans-serif' }}
                >
                  Select a Channel
                </InputLabel>
                <Select
                  labelId="channel-select-label"
                  id="channel-select"
                  value={channel || ''}
                  label="Select a Channel"
                  onChange={(e) => setChannel(e.target.value)}
                  sx={{
                    color: '#9AC0CD',
                    backgroundColor: 'rgba(13, 27, 42, 0.8)',
                    fontFamily: 'Poppins, sans-serif',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#607080',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#607080',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: '#081529', // Background color for the dropdown options
                        color: '#E0E4ED', // Text color for dropdown options
                      },
                    },
                  }}
                >
                  <MenuItem value="">
                    <em style={{ color: '#E0E4ED' }}>Select a Channel</em>
                  </MenuItem>
                  {channels.map((ch) => (
                    <MenuItem
                      key={ch.id}
                      value={ch.id}
                      sx={{
                        color: '#E0E4ED', // Color for each menu item text
                        fontFamily: 'Poppins, sans-serif',
                        '&:hover': {
                          backgroundColor: '#0D1F3A', // Slightly different background on hover
                        },
                      }}
                    >
                      {ch.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {tabValue === 1 && (
              <>
                <TextField
                  label="New Channel Name"
                  value={newChannelName}
                  onChange={(e) => {
                    let input = e.target.value;
                    console.log('Raw Input:', input); // Log raw input

                    // Replace spaces with hyphens
                    input = input.replace(/\s+/g, '-');
                    console.log('After replacing spaces:', input); // Log after replacing spaces

                    // Replace multiple hyphens with a single hyphen
                    input = input.replace(/-+/g, '-');
                    console.log('After replacing multiple hyphens:', input); // Log after replacing multiple hyphens

                    // Remove leading hyphens only
                    input = input.replace(/^-+/g, '');
                    console.log('After trimming leading hyphens:', input); // Log after trimming hyphens

                    // Convert to lowercase
                    input = input.toLowerCase();
                    console.log('Sanitized Channel Name:', input); // Final sanitized name

                    setNewChannelName(input);
                  }}
                  fullWidth
                  InputProps={{
                    style: {
                      color: '#E0E4ED',
                      backgroundColor: '#132A47',
                      borderRadius: '8px',
                    },
                  }}
                  InputLabelProps={{
                    style: {
                      color: '#D1E5F0',
                    },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#607080',
                      },
                      '&:hover fieldset': {
                        borderColor: '#9AC0CD',
                      },
                    },
                  }}
                />
                <FormControl fullWidth>
                  <InputLabel
                    id="category-select-label"
                    sx={{ color: '#A1D1F0', fontFamily: 'Poppins, sans-serif' }}
                  >
                    Category
                  </InputLabel>
                  <Select
                    labelId="category-select-label"
                    id="category-select"
                    value={selectedCategoryId || ''}
                    label="Category"
                    onChange={(e) => setSelectedCategoryId(e.target.value)}
                    sx={{
                      color: '#A1D1F0',
                      backgroundColor: '#0D1F3A',
                      fontFamily: 'Poppins, sans-serif',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#607080',
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          backgroundColor: '#081529', // Background color for the dropdown options
                          color: '#E0E4ED', // Text color for dropdown options
                        },
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em style={{ color: '#E0E4ED' }}>No Category</em>
                    </MenuItem>
                    {categories.map((category) => (
                      <MenuItem
                        key={category.id}
                        value={category.id}
                        sx={{
                          color: '#E0E4ED', // Color for each menu item text
                          fontFamily: 'Poppins, sans-serif',
                          '&:hover': {
                            backgroundColor: '#0D1F3A', // Slightly different background on hover
                          },
                        }}
                      >
                        {category.name}
                      </MenuItem>
                    ))}
                    <MenuItem value="create_new_category">
                      <em style={{ color: '#E0E4ED' }}>Create New Category</em>
                    </MenuItem>
                  </Select>
                  <Typography variant="caption" sx={{ color: '#A1D1F0', fontFamily: 'Poppins, sans-serif' }}>
                    Select an existing category or create a new one.
                  </Typography>
                </FormControl>
                {selectedCategoryId === 'create_new_category' && (
                  <TextField
                    label="New Category Name"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    fullWidth
                    InputProps={{
                      style: {
                        color: '#E0E4ED',
                        backgroundColor: '#132A47',
                        borderRadius: '8px',
                      },
                    }}
                    InputLabelProps={{
                      style: {
                        color: '#D1E5F0',
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#607080',
                        },
                        '&:hover fieldset': {
                          borderColor: '#9AC0CD',
                        },
                      },
                    }}
                  />
                )}
                <Button
                  variant="outlined"
                  sx={{
                    borderColor: '#A1D1F0',
                    color: '#A1D1F0',
                    mt: 2,
                    borderRadius: '8px',
                    fontFamily: 'Poppins, sans-serif',
                    '&:hover': {
                      borderColor: '#9AC0CD',
                      backgroundColor: 'rgba(154, 192, 205, 0.1)',
                    },
                  }}
                  onClick={handleCreateChannel}
                  disabled={isCreating} // Disable button while creating
                >
                  {isCreating ? 'Creating...' : 'Create Channel'}
                </Button>
              </>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

// Define PropTypes for ChannelConfiguration (optional but recommended)
ChannelConfiguration.propTypes = {
  channels: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      parent_id: PropTypes.string,
    })
  ).isRequired,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  selectedGuild: PropTypes.string.isRequired,
  accessToken: PropTypes.string.isRequired,
  channel: PropTypes.string.isRequired,
  setChannel: PropTypes.func.isRequired,
  newChannelName: PropTypes.string.isRequired,
  setNewChannelName: PropTypes.func.isRequired,
  selectedCategoryId: PropTypes.string.isRequired,
  setSelectedCategoryId: PropTypes.func.isRequired,
  newCategoryName: PropTypes.string.isRequired,
  setNewCategoryName: PropTypes.func.isRequired,
  refreshChannelsAndCategories: PropTypes.func.isRequired, // Define PropType for the refresh function
};

export default ChannelConfiguration;

